import type { RoutingMode } from './enums';

/**
 * PageIdentity — one implementation shared by SDK, API, filters and exports.
 *
 * Saved designs:  rel:<pageRevisionId>, resolved through the release mapping (never hostnames).
 * Live websites:  live:<integrationId>:<origin><path>?<allowlisted query>#<route>
 */

export const REVIEW_FRAGMENT_KEYS = ['__rv', 'h'] as const;

export const TRACKING_PARAM_PATTERNS: readonly RegExp[] = [
  /^utm_/i,
  /^gclid$/i,
  /^gbraid$/i,
  /^wbraid$/i,
  /^fbclid$/i,
  /^msclkid$/i,
  /^mc_(cid|eid)$/i,
  /^_ga$/i,
  /^_gl$/i,
  /^yclid$/i,
  /^ref$/i,
  /^igshid$/i,
];

const SENSITIVE_PARAM = /(token|auth|session|password|secret|key|code|sig|signature)/i;

/** Normalise a path inside an uploaded build: decoded, "/"-rooted, directory index folded. */
export function normalizePreviewPath(input: string): string {
  let p = input.split('#')[0]!.split('?')[0]!;
  try {
    p = decodeURIComponent(p);
  } catch {
    /* keep raw */
  }
  p = p.replace(/\\/g, '/');
  if (!p.startsWith('/')) p = '/' + p;
  const parts: string[] = [];
  for (const seg of p.split('/')) {
    if (seg === '' || seg === '.') continue;
    if (seg === '..') {
      parts.pop();
      continue;
    }
    parts.push(seg.normalize('NFC'));
  }
  let out = '/' + parts.join('/');
  if (out.toLowerCase().endsWith('/index.html')) out = out.slice(0, -'index.html'.length);
  else if (out.toLowerCase().endsWith('/index.htm')) out = out.slice(0, -'index.htm'.length);
  return out;
}

/** Candidate file paths (manifest keys, no leading slash) for a request path. */
export function candidateFilesForPath(requestPath: string): string[] {
  const norm = normalizePreviewPath(requestPath);
  const bare = norm.replace(/^\//, '');
  if (norm.endsWith('/')) return [bare + 'index.html', bare + 'index.htm'];
  return [bare, bare + '.html', bare + '/index.html'];
}

export function releasePageKey(pageRevisionId: string): string {
  return `rel:${pageRevisionId}`;
}

export interface LiveIdentityOptions {
  integrationId: string;
  routingMode: RoutingMode;
  queryAllowlist: readonly string[];
}

export interface LiveIdentity {
  pageKey: string;
  origin: string;
  path: string;
  query: string; // "" or "a=1&b=2"
  route: string; // "" or "/products"
  anchor: string; // section anchor, not part of identity
  sanitizedUrl: string;
}

/** Returns the hash route if the fragment is a route, else null (plain section anchor). */
export function routeFromFragment(fragment: string, mode: RoutingMode): string | null {
  const f = fragment.startsWith('#') ? fragment.slice(1) : fragment;
  if (!f) return null;
  const withoutReview = stripReviewParamsFromFragment(f);
  if (!withoutReview) return null;
  if (mode === 'history') return null;
  if (mode === 'hash') return withoutReview.startsWith('/') || withoutReview.startsWith('!/') ? withoutReview.replace(/^!/, '') : '/' + withoutReview;
  // auto
  if (withoutReview.startsWith('/') || withoutReview.startsWith('!/')) return withoutReview.replace(/^!/, '');
  return null;
}

function stripReviewParamsFromFragment(f: string): string {
  if (f.startsWith('__rv=')) return '';
  return f;
}

export function isTrackingParam(name: string): boolean {
  return TRACKING_PARAM_PATTERNS.some((r) => r.test(name));
}

export function liveIdentity(rawUrl: string, opts: LiveIdentityOptions): LiveIdentity {
  const u = new URL(rawUrl);
  const allow = new Set(opts.queryAllowlist.map((s) => s.toLowerCase()));
  const kept: [string, string][] = [];
  u.searchParams.forEach((v, k) => {
    const lk = k.toLowerCase();
    if (!allow.has(lk)) return;
    if (isTrackingParam(k) || SENSITIVE_PARAM.test(k) || REVIEW_FRAGMENT_KEYS.includes(lk as never)) return;
    kept.push([k, v]);
  });
  kept.sort((a, b) => (a[0] === b[0] ? a[1].localeCompare(b[1]) : a[0].localeCompare(b[0])));
  const query = kept.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&');
  let path = u.pathname || '/';
  if (path.length > 1 && path.endsWith('/')) path = path.slice(0, -1);
  const routeRaw = routeFromFragment(u.hash, opts.routingMode);
  let route = '';
  if (routeRaw) {
    // A hash route may carry its own "?query" — keep only allowlisted params there too.
    const [rp, rq] = routeRaw.split('?');
    route = rp || '/';
    if (rq) {
      const rs = new URLSearchParams(rq);
      const rk: string[] = [];
      rs.forEach((v, k) => {
        if (allow.has(k.toLowerCase()) && !isTrackingParam(k) && !SENSITIVE_PARAM.test(k)) rk.push(`${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
      });
      rk.sort();
      if (rk.length) route += '?' + rk.join('&');
    }
  }
  const anchor = !routeRaw && u.hash && !u.hash.startsWith('#__rv=') ? u.hash.slice(1) : '';
  const pageKey = `live:${opts.integrationId}:${u.origin}${path}${query ? '?' + query : ''}${route ? '#' + route : ''}`;
  const sanitizedUrl = `${u.origin}${u.pathname}${query ? '?' + query : ''}${route ? '#' + route : anchor ? '#' + anchor : ''}`;
  return { pageKey, origin: u.origin, path, query, route, anchor, sanitizedUrl };
}

/**
 * Pages the review toolbar cannot open — Shopify's checkout, the new customer accounts, the order
 * status page (Shopify serves them itself, without the theme's snippet). Their comments are made from
 * any page of the review and kept on a stand-in address of the website (/__rv-page/<slug>), with a
 * screenshot of the page; they are shown by name ("Checkout"), never as an address to open.
 */
export const OFF_PAGE_PREFIX = '/__rv-page/';
export const OFF_PAGES = [
  { slug: 'checkout', name: 'Checkout' },
  { slug: 'thank-you', name: 'Thank you / Order status' },
  { slug: 'customer-login', name: 'Customer login' },
  { slug: 'account', name: 'Customer account' },
  { slug: 'orders', name: 'Orders' },
  { slug: 'order-details', name: 'Order details' },
] as const;

/** "Gift card page" → "gift-card-page" (letters and digits, at most 40). */
export const offPageSlug = (name: string) =>
  name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
    .replace(/-+$/, '');

/** The stand-in address of such a page on this website. */
export const offPageUrl = (origin: string, slug: string) => `${origin}${OFF_PAGE_PREFIX}${slug}`;

/** The name of such a page from its address ("Checkout"); null for a real page. */
export function offPageName(url: string | null | undefined): string | null {
  if (!url) return null;
  let path = url;
  try {
    path = new URL(url).pathname;
  } catch {
    /* a path already */
  }
  if (!path.startsWith(OFF_PAGE_PREFIX)) return null;
  const slug = path.slice(OFF_PAGE_PREFIX.length).replace(/\/+$/, '');
  const known = OFF_PAGES.find((p) => p.slug === slug);
  if (known) return known.name;
  const words = slug.replace(/-+/g, ' ').trim();
  return words ? words[0]!.toUpperCase() + words.slice(1) : 'Other page';
}

// Shopify's own link-tracking parameters (search and collection result clicks).
const SHOP_TRACKING = /^(_pos|_sid|_ss|_psq|_fid|_v)$/i;
const PERSONAL_PARAM = /(email|phone)/i;

/**
 * The page a followed person is on, for a follower to open: the whole address (every query
 * parameter — a product's variant, a collection's page, sort and filters — and the #part), minus
 * tracking, private and review parameters. Live cursors' follow uses it.
 */
export function followUrl(rawUrl: string): string | null {
  let u: URL;
  try {
    u = new URL(rawUrl);
  } catch {
    return null;
  }
  const kept: [string, string][] = [];
  u.searchParams.forEach((v, k) => {
    const lk = k.toLowerCase();
    if (isTrackingParam(k) || SHOP_TRACKING.test(k) || SENSITIVE_PARAM.test(k) || PERSONAL_PARAM.test(k) || lk === '__rv' || lk === '__rv_review' || lk === 'h') return;
    kept.push([k, v]);
  });
  const q = new URLSearchParams(kept).toString();
  const hash = u.hash && !u.hash.startsWith('#__rv=') ? u.hash : '';
  const out = `${u.origin}${u.pathname}${q ? '?' + q : ''}${hash}`;
  return out.length <= 3000 ? out : `${u.origin}${u.pathname}`;
}

/** Exact origin normalisation for registration (scheme + host + port, no path). */
export function normalizeOrigin(input: string): string | null {
  try {
    const u = new URL(input);
    if (u.protocol !== 'https:' && u.protocol !== 'http:') return null;
    if (u.username || u.password) return null;
    return u.origin;
  } catch {
    return null;
  }
}

/* ---------- launch fragment helpers (portal side builds, SDK side parses) ---------- */

export function base64UrlEncodeString(s: string): string {
  const bytes = new TextEncoder().encode(s);
  let bin = '';
  bytes.forEach((b) => (bin += String.fromCharCode(b)));
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function base64UrlDecodeString(s: string): string {
  const pad = s.length % 4 ? '='.repeat(4 - (s.length % 4)) : '';
  const bin = atob(s.replace(/-/g, '+').replace(/_/g, '/') + pad);
  const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

/**
 * An app page inside the Shopify admin (admin.shopify.com/store/<shop>/apps/<app>/…, or the older
 * <shop>.myshopify.com/admin/apps/<app>/…). Shopify shows the app in a frame from the app's own
 * address and passes the path and query on to it — but not the #fragment.
 */
export function isShopifyAdminAppUrl(url: string): boolean {
  try {
    const u = new URL(url);
    if (u.hostname === 'admin.shopify.com') return /^\/store\/[^/]+\/apps\/[^/]+/.test(u.pathname);
    return /\.myshopify\.com$/.test(u.hostname) && /^\/admin\/apps\/[^/]+/.test(u.pathname);
  } catch {
    return false;
  }
}

/* ---------- Shopify app projects: which store an app page belongs to ---------- */

const SHOP_NAME = /^[a-z0-9][a-z0-9-]{0,59}$/;

/**
 * "<name>.myshopify.com" from a store name, its myshopify domain, or an address in its admin
 * (admin.shopify.com/store/<name>/…, <name>.myshopify.com/admin/…). null when it is none of them.
 */
export function normalizeShop(input: unknown): string | null {
  if (typeof input !== 'string') return null;
  let s = input.trim().toLowerCase();
  if (!s || s.length > 2000) return null;
  if (/^https?:\/\//.test(s) || s.startsWith('admin.shopify.com/')) {
    try {
      const u = new URL(/^https?:\/\//.test(s) ? s : `https://${s}`);
      if (u.hostname === 'admin.shopify.com') s = /^\/store\/([^/?#]+)/.exec(u.pathname)?.[1] ?? '';
      else s = u.hostname;
    } catch {
      return null;
    }
  }
  if (s.endsWith('.myshopify.com')) s = s.slice(0, -'.myshopify.com'.length);
  return SHOP_NAME.test(s) ? `${s}.myshopify.com` : null;
}

/**
 * The store an embedded app page is shown for: Shopify passes ?shop=<name>.myshopify.com, and
 * ?host= (base64 of "admin.shopify.com/store/<name>") on every load of the app.
 */
export function shopFromSearch(search: string): string | null {
  const p = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const shop = normalizeShop(p.get('shop'));
  if (shop) return shop;
  const host = p.get('host');
  if (!host || !/^[A-Za-z0-9+/_=-]{4,600}$/.test(host)) return null;
  try {
    return normalizeShop(`https://${base64UrlDecodeString(host.replace(/=+$/, ''))}`);
  } catch {
    return null;
  }
}

/**
 * The app inside another store's admin, made from the app's address in one store
 * (admin.shopify.com/store/<a>/apps/<app>/… → admin.shopify.com/store/<b>/apps/<app>).
 */
export function shopifyAppUrlForShop(appUrl: string, shop: string): string | null {
  const name = normalizeShop(shop)?.replace(/\.myshopify\.com$/, '');
  if (!name) return null;
  try {
    const u = new URL(appUrl);
    const app = u.hostname === 'admin.shopify.com' ? /^\/store\/[^/]+\/apps\/([^/?#]+)/.exec(u.pathname)?.[1] : /\.myshopify\.com$/.test(u.hostname) ? /^\/admin\/apps\/([^/?#]+)/.exec(u.pathname)?.[1] : undefined;
    return app ? `https://admin.shopify.com/store/${name}/apps/${app}` : null;
  } catch {
    return null;
  }
}

/**
 * A Shopify app's handle (the part after /apps/ in the admin), from the handle itself or from the
 * app's address in any store's admin. null when it is neither.
 */
export function shopifyAppHandleFrom(input: unknown): string | null {
  if (typeof input !== 'string') return null;
  const s = input.trim();
  if (/^https?:\/\//i.test(s) || s.toLowerCase().startsWith('admin.shopify.com/')) {
    try {
      const u = new URL(/^https?:\/\//i.test(s) ? s : `https://${s}`);
      const m = u.hostname === 'admin.shopify.com' ? /^\/store\/[^/]+\/apps\/([^/?#]+)/.exec(u.pathname) : /\.myshopify\.com$/.test(u.hostname) ? /^\/admin\/apps\/([^/?#]+)/.exec(u.pathname) : null;
      return m && /^[a-z0-9][a-z0-9_-]{0,99}$/i.test(m[1]!) ? m[1]!.toLowerCase() : null;
    } catch {
      return null;
    }
  }
  return /^[a-z0-9][a-z0-9_-]{0,99}$/i.test(s) ? s.toLowerCase() : null;
}

/** The app inside a store's admin, from the store and the app's handle. */
export function shopifyAdminAppUrl(shop: string, handle: string): string | null {
  const name = normalizeShop(shop)?.replace(/\.myshopify\.com$/, '');
  const h = shopifyAppHandleFrom(handle);
  return name && h ? `https://admin.shopify.com/store/${name}/apps/${h}` : null;
}

/** The Shopify admin itself (never the address an embedded app runs on). */
export const isShopifyAdminOrigin = (origin: string) => origin === 'https://admin.shopify.com' || /^https:\/\/[^/]+\.myshopify\.com$/.test(origin);

/** Build the new-tab launch URL, preserving the target's original fragment in `h`. */
export function buildLaunchUrl(targetUrl: string, code: string): string {
  const u = new URL(targetUrl);
  // Shopify admin apps: the code goes in the query, which Shopify passes on to the app's frame.
  if (isShopifyAdminAppUrl(targetUrl)) {
    u.searchParams.set('__rv', code);
    return u.toString();
  }
  const original = u.hash ? u.hash.slice(1) : '';
  u.hash = `__rv=${encodeURIComponent(code)}${original ? `&h=${base64UrlEncodeString(original)}` : ''}`;
  return u.toString();
}

/**
 * The website address a review link (/r/<key>_review) forwards to: `#__rv_review=1` tells the
 * snippet this browser belongs to a reviewer, so it shows "Log in to comment" (never to visitors).
 */
export function buildReviewerUrl(targetUrl: string): string {
  const u = new URL(targetUrl);
  if (isShopifyAdminAppUrl(targetUrl)) {
    u.searchParams.set('__rv_review', '1');
    return u.toString();
  }
  const original = u.hash ? u.hash.slice(1) : '';
  u.hash = `__rv_review=1${original ? `&h=${base64UrlEncodeString(original)}` : ''}`;
  return u.toString();
}

/** The launch code / reviewer marker when they came in the query (Shopify admin apps). */
export function parseLaunchQuery(search: string): { code: string | null; reviewer: boolean } {
  const p = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  return { code: p.get('__rv'), reviewer: p.has('__rv_review') };
}

/** The same address without the review query parameters (put back with history.replaceState). */
export function withoutLaunchQuery(href: string): string {
  const u = new URL(href);
  u.searchParams.delete('__rv');
  u.searchParams.delete('__rv_review');
  return u.pathname + (u.searchParams.toString() ? `?${u.searchParams}` : '') + u.hash;
}

/** Parse a launch fragment. Returns null when the fragment is not a review launch. */
export function parseLaunchFragment(hash: string): { code: string; originalFragment: string } | null {
  const f = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!f.startsWith('__rv=')) return null;
  const params = new URLSearchParams(f);
  const code = params.get('__rv');
  if (!code) return null;
  const h = params.get('h');
  let originalFragment = '';
  if (h) {
    try {
      originalFragment = base64UrlDecodeString(h);
    } catch {
      originalFragment = '';
    }
  }
  return { code, originalFragment };
}
